import type { OrganisationWebhookStatus } from '@risksmart-app/domain/src/types/consts/organisation-webhook-status';
import { CreateOrganisationWebhookDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface CreateWebhookInput {
  Title: string;
  Description: string | null;
  WebhookUrl: string;
  WebhookApiVersion: string;
  /** 'failed' is system-set, so only 'active' | 'paused' reach this input. */
  Status: Exclude<OrganisationWebhookStatus, 'failed'>;
  WebhookEventTypeIds: string[];
}

/**
 * Normalised create result shared by both data paths. The GraphQL path surfaces
 * the base64 secret as `SigningSecret`; the tRPC path as `plaintextSecret`.
 * Both branches converge on this common shape (via `mapGraphqlResponse` /
 * `mapResponse`) so the one-time reveal modal is agnostic to the active path.
 */
export interface CreateWebhookResult {
  plaintextSecret: string;
}

export const useCreateWebhook = createMutationHook({
  name: 'createWebhook',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.webhook.create.mutationOptions({ onSuccess }),
  mapInput: (input: CreateWebhookInput) => input,
  mapResponse: (result: { plaintextSecret: string }): CreateWebhookResult => ({
    plaintextSecret: result.plaintextSecret,
  }),
  invalidate: (trpc) => [trpc.frontend.webhook.list.queryKey()],
  graphqlDocument: CreateOrganisationWebhookDocument,
  graphqlVariables: (input) => ({ object: input }),
  mapGraphqlResponse: (data): CreateWebhookResult => {
    const plaintextSecret = data.createOrganisationWebhook?.SigningSecret;
    if (!plaintextSecret) {
      throw new Error('Failed to create webhook');
    }

    return { plaintextSecret };
  },
  evictFields: ['organisation_webhook'],
});
